import { useEffect, useState } from 'react';
import { api } from '../api';
import { Spinner, dayName, shortDate } from './ui';
import type { BatchOptions, Language } from '../types';

const T = {
  en: {
    title: 'Cook once, eat twice',
    close: 'Cancel',
    loading: 'Looking at the rest of the week…',
    lead: (meal: string) =>
      `Make extra ${meal} and serve it again later in the week. It gets shopped for once, cooked once, and the second day needs nothing.`,
    notSuitable: (meal: string) =>
      `${meal} does not reheat well enough to be worth doubling. Stews, sauces, curries and soups do; anything crisp or fresh does not.`,
    whichDay: 'Which day should it feed?',
    whatBecomes: 'And what should it become?',
    sameAgain: 'The same again',
    needsToo: (items: string[]) =>
      items.length === 0 ? 'nothing else to buy' : `plus ${items.slice(0, 3).join(', ')}`,
    back: 'Change day',
    fridge: 'fridge',
    freezer: 'freezer',
    keeps: (where: string) => `keeps in the ${where}`,
    extra: (n: number) => `${n} extra portions`,
    none: 'There is nothing later in the week it could feed.',
    why: 'Why this helps',
    whyText:
      'It also moves food past the shopping. Mince bought on Saturday cannot survive until Thursday — but a bolognese cooked on Monday can.',
  },
  fr: {
    title: 'Cuisiner une fois, manger deux fois',
    close: 'Annuler',
    loading: 'Analyse du reste de la semaine…',
    lead: (meal: string) =>
      `Préparez plus de ${meal} et resservez-le plus tard dans la semaine. Acheté une fois, cuisiné une fois, et le second jour ne demande rien.`,
    notSuitable: (meal: string) =>
      `${meal} ne se réchauffe pas assez bien. Les ragoûts, currys et soupes oui ; ce qui est croustillant ou frais non.`,
    whichDay: 'Quel jour doit-il nourrir ?',
    whatBecomes: 'Et il devient quoi ?',
    sameAgain: 'La même chose',
    needsToo: (items: string[]) =>
      items.length === 0 ? 'rien d’autre à acheter' : `plus ${items.slice(0, 3).join(', ')}`,
    back: 'Changer de jour',
    fridge: 'réfrigérateur',
    freezer: 'congélateur',
    keeps: (where: string) => `au ${where}`,
    extra: (n: number) => `${n} portions en plus`,
    none: 'Rien plus tard dans la semaine ne peut être nourri ainsi.',
    why: 'Pourquoi c’est utile',
    whyText:
      'Cela fait aussi voyager la nourriture au-delà des courses. La viande hachée du samedi ne tient pas jusqu’à jeudi — une bolognaise cuisinée lundi, si.',
  },
} as const;

/**
 * Setting up a batch (§29).
 *
 * Deliberately framed as a choice about a *day* rather than a quantity: the
 * family thinks "Monday's bolognese can do Thursday too", not "make 9 portions".
 * The portions follow from who is eating on the day chosen.
 */
export function BatchPicker({
  weekStart,
  assignmentKey,
  language,
  onLink,
  onClose,
}: {
  weekStart: string;
  assignmentKey: string;
  language: Language;
  onLink: (targetKey: string, becomesMealId: string) => void;
  onClose: () => void;
}) {
  const t = T[language];
  const [options, setOptions] = useState<BatchOptions | null>(null);
  // Two questions, in the order a person asks them: which night, then what.
  const [chosenDay, setChosenDay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .batchOptions(weekStart, assignmentKey, language)
      .then((result) => live && setOptions(result))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load'));
    return () => {
      live = false;
    };
  }, [weekStart, assignmentKey, language]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const usable = (options?.targets ?? []).filter((target) => !target.blockedBecause);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
      >
        <div className="sheet-head">
          <h2>{t.title}</h2>
          <button className="btn" onClick={onClose}>
            {t.close}
          </button>
        </div>

        <div className="sheet-body">
          {error && <p className="banner warn">{error}</p>}
          {!options && !error && (
            <div style={{ display: 'grid', placeItems: 'center', padding: 40, gap: 12 }}>
              <Spinner />
              <span className="dim">{t.loading}</span>
            </div>
          )}

          {options && !options.batchFriendly && (
            <p className="banner warn">{t.notSuitable(options.mealName)}</p>
          )}

          {options && options.batchFriendly && (
            <>
              <p className="dim" style={{ marginBottom: 16 }}>
                {t.lead(options.mealName)}
              </p>

              {usable.length === 0 ? (
                <p className="dim">{t.none}</p>
              ) : (
                <>
                  {chosenDay ? (
                    <>
                      <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                        <h3 className="sheet-group" style={{ flex: 1, margin: 0 }}>
                          {t.whatBecomes}
                        </h3>
                        <button className="btn small" onClick={() => setChosenDay(null)}>
                          {t.back}
                        </button>
                      </div>
                      {options.secondLives.map((life, index) => (
                        <button
                          key={life.mealId}
                          className="choice"
                          onClick={() => onLink(chosenDay, life.mealId)}
                        >
                          <span className="choice-main">
                            <span className="choice-name">
                              {index === 0 ? `${life.name} — ${t.sameAgain}` : life.name}
                            </span>
                            <span className="choice-desc">{life.description}</span>
                          </span>
                          <span
                            className={`choice-supply${life.extraIngredients.length === 0 ? ' ok' : ''}`}
                          >
                            {t.needsToo(life.extraIngredients)}
                          </span>
                        </button>
                      ))}
                    </>
                  ) : (
                    <>
                  <h3 className="sheet-group">{t.whichDay}</h3>
                  {usable.map((target) => (
                    <button
                      key={target.assignmentKey}
                      className="choice"
                      onClick={() => setChosenDay(target.assignmentKey)}
                    >
                      <span className="choice-main">
                        <span className="choice-name">
                          {dayName(target.date, language)} · {target.slotLabel}
                        </span>
                        <span className="choice-desc">
                          {shortDate(target.date, language)} ·{' '}
                          {t.extra(Math.round(target.extraPortions * 10) / 10)}
                        </span>
                      </span>
                      <span
                        className={`choice-supply${target.storage === 'fridge' ? ' ok' : ''}`}
                      >
                        {target.storage === 'freezer' ? '❄' : '🧊'}{' '}
                        {t.keeps(target.storage === 'freezer' ? t.freezer : t.fridge)}
                      </span>
                    </button>
                  ))}
                    </>
                  )}

                  <p className="dim" style={{ fontSize: 13, marginTop: 16 }}>
                    <strong>{t.why}.</strong> {t.whyText}
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
